import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Typography, Button, Form, Select, Menu, message, Input } from 'antd';
import { PTTGroup } from '../../../types/PTTGroup';
import { useAuthStore } from '../../../store/auth/auth';
import { usePTTGroupsStore } from '../../../store/pttGroups/usePTTGroupsStore';
import { ReactComponent as SearchImg } from 'utils/app/assets/icons/search.svg';

import { ReactComponent as EditImg } from 'utils/app/assets/icons/Edit.svg';
import { ReactComponent as DeleteImg } from 'utils/app/assets/icons/Delete.svg';

import MicrophoneImg from 'utils/app/assets/images/microphone.png';
import LogoImg from 'utils/app/assets/images/Logo placeholder.png';

import './style.css';
import ButtonAddPlus from '../../../components/buttons/buttonAddPlus/ButtonAddPlus';
import { Footer } from 'antd/lib/layout/layout';
import { Device } from '../../../types/Device';
import { CloseOutlined } from '@ant-design/icons';
import { GroupModal } from './modals/GroupModal';
import { DeviceByGroupStore } from '../../../store/devices/DeviceByGroupStore';
import { useOperator } from '../../../store/users/Operators';
import { DeleteGroupModal } from './modals/DeleteGroupModal';
import { MenuIcons } from './consts/MenuIcons';
import { CustomSubMenu } from './components/CustomSubMenu/CustomSubMenu';
import { RecordingAnimation } from './components/RecordingAnimation/RecordingAnimation';
import usePTTMessageStore, { PTTMessage } from './api/usePttMessageStore';
import { CurrentTarget, MessageList } from './components/MessageList/MessageList';
import { useUserStore } from '../../../store/users/UserStore';
import { safeTimestamp, safeTimestampTwo } from './helpers/formatDateTime';
import { getDeviceName } from './helpers/giveDeviceName';
import { PTT_WS_BASE_URL } from '../../../const/const';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const PTTDispatchV2: React.FC = () => {
    // Состояния для PTT функционала
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<PTTGroup | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [searchText, setSearchText] = useState('');
    const [currentTarget, setCurrentTarget] = useState<CurrentTarget | null>({
        type: 'group',
        name: 'unsigned',
        groupName: 'unsigned',
        id: '00000000-0000-0000-0000-000000000000',
    });
    const [devicesunsigned, setDevicesunsigned] = useState<Device[]>([]);
    const [deleteGroupModal, setDeleteGroupModal] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [isWsConnected, setIsWsConnected] = useState(false);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 5;
    const reconnectInterval = useRef<NodeJS.Timeout>();

    // Состояния для администрирования
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isCreateGroupModalVisible, setIsCreateGroupModalVisible] = useState(false);
    const [formCreate] = Form.useForm();
    const [formEdit] = Form.useForm();
    const [editingGroup, setEditingGroup] = useState<PTTGroup | undefined>(undefined);

    // Refs
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const recordButtonRef = useRef<HTMLButtonElement | null>(null);
    const socket = useRef<WebSocket | null>(null);
    const mutableChunks = useRef<Blob[]>([]);

    // Store hooks
    const { SmartDVRToken, user } = useAuthStore();
    const { addMessage, fetchMessages, fetchMessagesByGroup } = usePTTMessageStore();
    const { groups, fetchGroups, updateGroup, removeGroup } = usePTTGroupsStore();
    const { fetchDevicesByStore, allDevices, fetchAllDevices } = DeviceByGroupStore();
    const { users: operators, fetchUsers: fetchOperators } = useOperator();
    const { users, fetchUsers } = useUserStore();

    // Формы
    const [createForm] = Form.useForm();

    const { incrementUnread, resetUnread, unreadCounts, addToPlayQueue, setPauseQueue } =
        usePTTMessageStore();

    useEffect(() => {
        if (currentTarget?.id) {
            resetUnread(currentTarget.id);
        }
    }, [currentTarget?.id]);

    useEffect(() => {
        if (!currentTarget || !user?.login || !SmartDVRToken) return;

        fetchAllDevices(SmartDVRToken, user?.login);
        console.log(currentTarget);
        const fetchData = async () => {
            await fetchMessages({
                data: {
                    deviceUID:
                        currentTarget.type === 'device'
                            ? currentTarget.id !== null
                                ? currentTarget.id
                                : undefined
                            : undefined,
                    groupUID:
                        currentTarget.id !== '00000000-0000-0000-0000-000000000000' &&
                        currentTarget.type === 'group'
                            ? currentTarget.groupId || currentTarget.id
                            : undefined,
                    start: '2025-01-01T00:00:00Z',
                    end: '2099-01-01T00:00:00Z',
                },
                SmartDVRToken,
                userLogin: user.login,
                devices: allDevices,
                pttGroups: groups,
                operators,
                users,
                count: 80,
            });
        };

        fetchData();
    }, [currentTarget, SmartDVRToken, user?.login]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isRecording) {
            interval = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } else {
            setRecordingTime(0);
        }
        return () => clearInterval(interval);
    }, [isRecording]);

    useEffect(() => {
        const loadData = async () => {
            try {
                // Выполняем все запросы параллельно
                const [groupsData, devicesunsigned, operators] = await Promise.all([
                    fetchGroups(),
                    fetchDevicesByStore(
                        '00000000-0000-0000-0000-000000000002',
                        SmartDVRToken,
                        user?.login!,
                    ),
                    fetchOperators(),
                ]);
                fetchAllDevices(SmartDVRToken, user?.login!);
                setDevicesunsigned(devicesunsigned ?? []);
                fetchUsers();
                // Остальная инициализация
                getMicrophoneAccess();
            } catch (error) {
                console.error('Error loading data:', error);
                message.error('Ошибка загрузки данных');
            }
        };

        if (user?.login && SmartDVRToken) {
            loadData();
        }
    }, [user?.login, SmartDVRToken]);

    const setupWebSocket = useCallback(() => {
        if (!user?.uid || !SmartDVRToken) return;

        if (socket.current) {
            socket.current.close(1000, 'Reconnecting');
        }

        const url = `${PTT_WS_BASE_URL}/${user.uid}/${SmartDVRToken}`;
        const ws = new WebSocket(url);
        socket.current = ws;

        socket.current.binaryType = 'arraybuffer';

        socket.current.onopen = () => {
            console.log('WebSocket connected');
            setIsWsConnected(true);
            reconnectAttempts.current = 0;

            if (currentTarget) {
                startConversationTo();
            }
        };

        socket.current.onmessage = async (msg: MessageEvent<ArrayBuffer>) => {
            try {
                const uint8 = new Uint8Array(msg.data);
                const decoder = new TextDecoder('utf-8');
                console.log(msg);
                try {
                    // Сначала пробуем декодировать как чистый JSON
                    const text = decoder.decode(uint8);
                    const data = JSON.parse(text);
                    await handleJsonMessage(data);
                    return;
                } catch (jsonError) {
                    // Если не получилось - обрабатываем как смешанное сообщение (JSON + бинарные данные)
                    await handleMixedMessage(uint8, decoder);
                }
            } catch (err) {
                console.error('🛑 Ошибка обработки WebSocket сообщения:', err);
            }
        };

        socket.current.onerror = (e) => {
            console.error('[WS] error', e);
            setIsWsConnected(false);
        };

        socket.current.onclose = (e) => {
            console.warn(`[WS] closed, code: ${e.code}, reason: ${e.reason}`);
            setIsWsConnected(false);
            if (e.code !== 1000) {
                const delay = Math.min(5000, reconnectAttempts.current * 1000);
                reconnectAttempts.current += 1;

                setTimeout(() => {
                    if (reconnectAttempts.current <= maxReconnectAttempts) {
                        setupWebSocket();
                    }
                }, delay);
            }
        };

        return () => {
            socket.current?.close();
        };
    }, [user?.uid, SmartDVRToken, currentTarget]);

    useEffect(() => {
        setupWebSocket();

        return () => {
            // Очистка при размонтировании
            if (socket.current) {
                socket.current.close(1000, 'Component unmounted');
            }
            if (mediaRecorder?.state === 'recording') {
                mediaRecorder.stop();
            }
            if (mediaRecorder?.stream) {
                mediaRecorder.stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [setupWebSocket]);
    const getRealtimeTargetId = (data: any) => {
        if (data.recipient_type === 'group') {
            return data.group_uid || data.ptt_group_uid || data.recipient_uid;
        }

        return data.sender_uid || data.receiver_uid || data.recipient_uid;
    };

    const isCurrentRealtimeTarget = (targetId?: string) => {
        if (!targetId || !currentTarget) return false;

        return currentTarget.id === targetId || currentTarget.groupId === targetId;
    };

    const buildRealtimeMessage = (data: any, audioBlobUrl?: string): PTTMessage => {
        const currentGroup =
            currentTarget?.type === 'group'
                ? groups.find((g) => g.uid === currentTarget.id || g.uid === currentTarget.groupId)
                : undefined;
        const fallbackDevice =
            currentGroup?.devices?.length === 1 ? currentGroup.devices[0] : undefined;
        const senderUid = data.sender_uid || data.receiver_uid || fallbackDevice?.UID || '';
        const senderType = data.sender_type || data.receiver_type || 'device';
        const groupUid =
            data.group_uid ||
            data.ptt_group_uid ||
            (data.recipient_type === 'group' ? data.recipient_uid : undefined) ||
            (currentTarget?.type === 'group' ? currentTarget.id : undefined);
        const recipientType = data.recipient_type || (groupUid ? 'group' : 'device');
        const recipientUid = recipientType === 'group' ? groupUid || '' : data.recipient_uid || '';
        const isOutgoing = senderType === 'operator' && senderUid === user?.uid;
        const device =
            senderType === 'device' ? getDeviceName(allDevices, senderUid) || fallbackDevice : undefined;
        const operator =
            senderType === 'operator'
                ? operators?.find((u) => u.uid === senderUid) ||
                  users?.find((u) => u.uid === senderUid)
                : undefined;
        const group = groupUid ? groups.find((g) => g.uid === groupUid) || currentGroup : undefined;

        return {
            id: data.uid || crypto.randomUUID(),
            audioUrl: data.file_url || '',
            audioBlobUrl,
            direction: isOutgoing ? 'outgoing' : 'incoming',
            sender: {
                id: senderUid,
                name:
                    operator?.name ||
                    device?.name ||
                    (senderType === 'operator' ? 'Оператор не опознан' : 'Устройство не опознано'),
                type: senderType,
            },
            recipient: {
                id: recipientUid,
                type: recipientType,
            },
            timestamp: audioBlobUrl
                ? safeTimestampTwo(data.date_time)
                : safeTimestamp(data.date_time),
            isPlayed: isOutgoing,
            fileUrl: data.file_url,
            group,
        };
    };

    // Обработчик чистых JSON сообщений
    const handleJsonMessage = async (data: any) => {
        const targetId = getRealtimeTargetId(data);
        const message = buildRealtimeMessage(data);

        if (!message.isPlayed && targetId && !isCurrentRealtimeTarget(targetId)) {
            incrementUnread(targetId);
        }

        handleNewMessage(message);
        // addMessage(message);
    };

    // Обработчик смешанных сообщений (JSON + аудио)
    const handleMixedMessage = async (uint8: Uint8Array, decoder: TextDecoder) => {
        // Ищем разделитель между JSON и бинарными данными (обычно это \n)
        let index = 0;
        while (index < uint8.length && uint8[index] !== 10) index++;

        if (index >= uint8.length) {
            throw new Error('Invalid message format: no separator found');
        }

        // Парсим JSON часть
        const jsonPart = uint8.subarray(0, index);
        const jsonText = decoder.decode(jsonPart);
        const data = JSON.parse(jsonText);

        // Обрабатываем бинарную часть (аудио)
        const binaryPart = uint8.subarray(index + 1);
        const audioBlob = new Blob([binaryPart], { type: 'audio/webm;codecs=opus' });

        // Обновляем счетчик непрочитанных
        const targetId = getRealtimeTargetId(data);
        if (targetId && !isCurrentRealtimeTarget(targetId)) {
            incrementUnread(targetId);
        }
        console.log([currentTarget?.id, targetId, data]);

        // console.log(currentTarget?.id != targetId);

        const message = buildRealtimeMessage(data, URL.createObjectURL(audioBlob));
        console.log(message);

        handleNewMessage(message);
        // addMessage(message);
    };

    // Функции PTT
    const getMicrophoneAccess = async () => {
        try {
            // Полная очистка предыдущего состояния
            if (mediaRecorder) {
                if (mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                }
                mediaRecorder.stream?.getTracks().forEach((track) => {
                    track.stop();
                    track.enabled = false;
                });
                setMediaRecorder(null);
            }

            // Ждем полной очистки
            await new Promise((resolve) => setTimeout(resolve, 300));

            // ПРОСТЕЙШИЕ настройки микрофона
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    // Убираем все продвинутые настройки
                },
                video: false,
            });

            console.log('Microphone access granted');

            // Пробуем разные MIME types по порядку
            const mimeTypes = [
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/mp4',
                'audio/ogg;codecs=opus',
                '', // Пустая строка - браузер сам выберет
            ];

            let recorder: MediaRecorder | null = null;
            let selectedType = '';

            // Ищем работающий MIME type
            for (const mimeType of mimeTypes) {
                try {
                    if (mimeType && !MediaRecorder.isTypeSupported(mimeType)) {
                        console.warn(`MIME type not supported: ${mimeType}`);
                        continue;
                    }

                    const options = mimeType ? { mimeType } : {};
                    recorder = new MediaRecorder(stream, options);

                    // Проверяем, что Recorder создался
                    if (recorder) {
                        selectedType = mimeType || 'default';
                        console.log(`✅ Using MIME type: ${selectedType}`);
                        break;
                    }
                } catch (error) {
                    console.warn(`Failed with MIME type ${mimeType}:`, error);
                    continue;
                }
            }

            if (!recorder) {
                throw new Error('No suitable MIME type found');
            }

            // Настройка обработчиков
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    console.log(`Audio chunk: ${e.data.size} bytes`);
                    mutableChunks.current.push(e.data);
                }
            };

            recorder.onerror = (e) => {
                console.error('MediaRecorder error:', e);
                setIsRecording(false);
            };

            recorder.onstop = () => {
                console.log('MediaRecorder stopped, total chunks:', mutableChunks.current.length);
                // Останавливаем треки только когда действительно закончили
                setTimeout(() => {
                    stream.getTracks().forEach((track) => track.stop());
                }, 1000);
            };

            setMediaRecorder(recorder);
            return true;
        } catch (err: any) {
            console.error('❌ Microphone access failed:', err);

            // Показываем понятную ошибку пользователю
            if (err?.name === 'NotAllowedError') {
                message.error(
                    'Доступ к микрофону запрещен. Разрешите доступ в настройках браузера',
                );
            } else if (err?.name === 'NotFoundError') {
                message.error('Микрофон не найден. Проверьте подключение микрофона');
            } else {
                message.error('Ошибка доступа к микрофону: ' + err?.message);
            }

            setMediaRecorder(null);
            return false;
        }
    };
    // const getMicrophoneAccess = async () => {
    //     try {
    //         // Полностью очищаем предыдущий MediaRecorder
    //         if (mediaRecorder) {
    //             if (isRecording || mediaRecorder.state === 'recording') {
    //                 mediaRecorder.stop();
    //             }
    //             mediaRecorder.stream.getTracks().forEach((track) => {
    //                 track.stop();
    //                 track.enabled = false;
    //             });
    //         }
    //
    //         await new Promise((resolve) => setTimeout(resolve, 100));
    //
    //         const options = {
    //             mimeType: 'audio/webm;codecs=opus',
    //         };
    //         const stream = await navigator.mediaDevices.getUserMedia({
    //             video: false,
    //             audio: { channelCount: 1, sampleRate: 16000 },
    //         });
    //         const recorder = new MediaRecorder(stream, options);
    //
    //         recorder.ondataavailable = (e) => {
    //             if (e.data.size > 0) mutableChunks.current.push(e.data);
    //         };
    //
    //         recorder.onerror = (e) => {
    //             console.error('MediaRecorder error:', e);
    //             setIsRecording(false);
    //         };
    //
    //         setMediaRecorder(recorder);
    //     } catch (err) {
    //         console.error('Error accessing microphone:', err);
    //         message.error('Ошибка доступа к микрофону');
    //     }
    // };

    const toggleRecording = async () => {
        if (!mediaRecorder) {
            await getMicrophoneAccess();
            if (!mediaRecorder) {
                message.error('Микрофон не доступен');
                return;
            }
        }

        // Проверяем состояние MediaRecorder
        if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            setIsRecording(false);
            return;
        }

        if (!currentTarget) {
            message.warning('Выберите получателя перед отправкой');
            return;
        }

        mediaRecorder.onstop = async () => {
            if (mutableChunks.current.length === 0) return;
            const timestamp = new Date().toISOString();
            console.log(timestamp);
            const audioBlob = new Blob(mutableChunks.current, { type: 'audio/webm;codecs=opus' });
            mutableChunks.current = [];
            console.log(currentTarget);
            startConversationTo();
            console.log(currentTarget);

            socket.current?.send(audioBlob);
        };

        const store = usePTTMessageStore.getState();
        store.pauseCurrentAudio();
        store.setIsManualPlaying(false);
        store.clearPlayQueue();

        try {
            await new Promise((resolve) => setTimeout(resolve, 50));
            mutableChunks.current = [];
            setIsRecording(true);
            mediaRecorder.start(); // Запускаем запись
        } catch (error) {
            console.error('Error starting MediaRecorder:', error);
            setIsRecording(false);
            message.error('Ошибка запуска записи. Попробуйте снова.');

            // Пересоздаем MediaRecorder при ошибке
            await getMicrophoneAccess();
        }

        // if (isRecording) {
        //     mediaRecorder.stop();
        //     setIsRecording(false);
        //     // setCurrentTarget(currentTarget);
        // } else {
        //     const store = usePTTMessageStore.getState();
        //     store.pauseCurrentAudio();
        //     store.setIsManualPlaying(false);
        //     store.clearPlayQueue();
        //
        //     mutableChunks.current = [];
        //     setIsRecording(true);
        //     mediaRecorder.start();
        // }
    };

    const startConversationTo = () => {
        if (!currentTarget) return;

        console.log('Subscribing to:', [currentTarget, socket.current]);
        if (currentTarget.type == 'group') {
            if (currentTarget.name == 'unsigned') {
                socket.current?.send(`room_unsigned`);
            } else {
                socket.current?.send(`room_${currentTarget.groupName || currentTarget.name}`);
            }
        } else {
            socket.current?.send(`device_${currentTarget?.deviceDid}`);
        }
    };

    // const selectTarget = (group: PTTGroup | null, device?: Device) => {
    //     if (device?.UID) {
    //         // Находим группу устройства
    //         const deviceGroup = groups.find((g) => g.devices?.some((d) => d.UID === device.UID));
    //
    //         setSelectedGroup(null);
    //         setSelectedDevice(device || null);
    //         setCurrentTarget({
    //             type: 'device',
    //             name: device?.name || device.UID,
    //             id: device.UID,
    //             online: device.online,
    //             groupId: deviceGroup?.uid,
    //             groupName: deviceGroup?.name,
    //             deviceDid: device.DID,
    //         });
    //     } else if (group) {
    //         setSelectedGroup(group);
    //         setSelectedDevice(null);
    //         setCurrentTarget({
    //             type: 'group',
    //             name: group?.name,
    //             groupName: group?.name,
    //             id: group?.uid || '',
    //         });
    //     } else {
    //         // Обработка случая несортированных устройств
    //         setSelectedGroup(null);
    //         setSelectedDevice(null);
    //         setCurrentTarget({
    //             type: 'group',
    //             name: 'Несортированные устройства',
    //             id: '00000000-0000-0000-0000-000000000000', // специальный идентификатор
    //             groupName: 'Несортированные устройства',
    //         });
    //     }
    // };

    const selectTarget = (group: PTTGroup | null, device?: Device) => {
        console.log(group, device);
        if (device?.UID) {
            // Находим группу устройства
            const deviceGroup = groups.find((g) => g.devices?.some((d) => d.UID === device.UID));

            setSelectedGroup(null);
            setSelectedDevice(device || null);
            setCurrentTarget({
                type: 'device',
                name: device?.name || device.UID,
                id: device.UID,
                online: device.online,
                groupId: deviceGroup?.uid,
                groupName: deviceGroup?.name,
                deviceDid: device.DID,
            });
        } else {
            setSelectedGroup(group);
            setSelectedDevice(null);
            setCurrentTarget({
                type: 'group',
                name: group?.name || 'unsigned',
                groupName: group?.name || 'unsigned',
                id: group?.uid || '00000000-0000-0000-0000-000000000000',
            });
        }
    };

    const showEditModal = (group: PTTGroup) => {
        setEditingGroup(group);
        setIsEditModalVisible(true);
    };

    // Функции администрирования
    const handleCreateGroup = async (values: any) => {
        try {
            if (!user?.uid) return;

            const response = await usePTTGroupsStore.getState().addGroup(
                {
                    uid: '',
                    name: values.name,
                    userUID: user.uid,
                    user: user,
                    // devices: allDevices?.filter((device) => values?.devices?.includes(device.UID)),
                    // viewers: values.viewers ? values.viewers.map((uid: string) => ({ uid })) : [],
                },
                allDevices?.filter((device) => values?.devices?.includes(device.UID)),
                values.viewers ? users.filter((user) => values.viewers.includes(user.uid)) : [],
                // operators?.filter((user) => values?.viewers?.includes(user.uid)),
            );

            if (response?.success) {
                message.success('Группа создана успешно!');
                createForm.resetFields();
                setIsCreateGroupModalVisible(false);
                fetchGroups();
            } else {
                message.error(response?.error || 'Ошибка создания группы!');
            }
        } catch (error) {
            message.error('Ошибка при создании группы!');
            console.error(error);
        }
    };

    const handleEditGroup = async (values: any) => {
        if (!editingGroup) return;

        try {
            await updateGroup({
                uid: editingGroup.uid,
                name: values.name,
                devices: allDevices.filter((d) => values.devices.includes(d.UID)),
                viewers: values.viewers ? values.viewers.map((uid: string) => ({ uid })) : [],
            });

            message.success('Группа успешно обновлена!');
            setIsEditModalVisible(false);
            fetchGroups();
        } catch (error) {
            message.error('Ошибка при обновлении группы!');
            console.error(error);
        }
    };

    const handleDeleteGroup = async () => {
        if (!selectedGroup) return;

        try {
            await removeGroup(selectedGroup?.uid);
            message.success('Группа удалена успешно!');
            setDeleteGroupModal(false);
            setEditingGroup(undefined);
            setCurrentTarget(null);
            setSelectedGroup(null);
        } catch (error) {
            message.error('Ошибка удаления группы!');
            console.error(error);
        }
    };

    const renderMenuIcon = (type: keyof typeof MenuIcons, online?: boolean) => {
        const IconComponent = MenuIcons[type];
        return (
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <IconComponent style={{ marginTop: -2 }} />
                {online !== undefined && renderStatusDot(online)}
            </span>
        );
    };

    const renderStatusDot = (online: boolean, small?: boolean) => {
        if (small) {
            return (
                <span
                    style={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: online ? '#5ABEA6' : 'red',
                        marginRight: 8,
                        marginBottom: 4,
                    }}
                />
            );
        } else {
            return (
                <span
                    style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: online ? '#5ABEA6' : 'red',
                        marginRight: 8,
                        marginBottom: 1,
                    }}
                />
            );
        }
    };

    const getAvailableDevices: any[] = useMemo(() => {
        // Получаем все UID устройств, которые уже в группах
        const usedDevices: Device[] = [];
        groups.forEach((group) => {
            group.devices?.forEach((device) => {
                usedDevices.push(device);
            });
        });
        // Получаем UID использованных устройств
        const usedDeviceUIDs = usedDevices.map((device) => device.UID);

        return allDevices.filter((device) => {
            return !usedDeviceUIDs.includes(device.UID);
        });
    }, [groups, allDevices]);

    const filteredGroups = useMemo(() => {
        if (!user?.uid) return [];

        return groups.filter((group) => {
            // Администратор видит все группы
            if (user.isAdmin) return true;

            // Проверяем, есть ли текущий оператор в списке allowedOperators
            group.viewers?.map((viewer) => {
                if (viewer.uid === user.uid) return true;
            }); // Создатель группы также имеет доступ
        });
    }, [groups, user]);

    const handleNewMessage = (message: PTTMessage) => {
        const store = usePTTMessageStore.getState();

        // Всегда добавляем сообщение в историю (если это не дубликат)
        if (!store.messages.some((m) => m.id === message.id && m.timestamp === message.timestamp)) {
            store.addMessage(message);
        }

        // Не добавляем в очередь если:
        // 1. Сейчас идет запись
        // 2. Сообщение не для текущего чата
        // 3. Это исходящее сообщение от оператора
        if (
            isRecording ||
            !isCurrentRealtimeTarget(message.group?.uid || message.recipient.id) ||
            (message.direction === 'outgoing' && message.sender.type === 'operator')
        ) {
            return;
        }

        // Добавляем в очередь только если ничего не играет
        if (!store.isManualPlay && !store.isPlaying) {
            store.addToPlayQueue(message);
        }

        // const store = usePTTMessageStore.getState();
        //
        // // Проверяем, активно ли сейчас воспроизведение
        // const isCurrentlyPlaying = store.isManualPlay || store.isPlaying;
        //
        // const shouldAddToQueue =
        //     currentTarget?.id === message.recipient.id &&
        //     !isCurrentlyPlaying &&
        //     !isRecording && // Добавляем только если ничего не играет
        //     !(message.direction === 'outgoing' && message.sender.type === 'operator');
        //
        // if (!store.messages.some((m) => m.id === message.id && m.timestamp === message.timestamp)) {
        //     store.addMessage(message);
        //     if (shouldAddToQueue) {
        //         store.addToPlayQueue(message);
        //     }
        // }
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Header
                style={{
                    backgroundColor: '#4D4E65',
                    color: '#fff',
                    height: 64,
                    padding: '0 0',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'fixed',
                    width: '100%',
                    top: 0,
                    zIndex: 1000,
                }}
            >
                <div className="logo_ptt">
                    <img className="img" src={LogoImg} alt="Logo" />
                </div>
                <Title level={3} style={{ color: '#fff', margin: 0 }}>
                    Радиочат
                </Title>
            </Header>

            <Layout className={'layout-sider-ptt'}>
                <Sider
                    width={250}
                    style={{
                        backgroundColor: 'var(--bg-surface)',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        position: 'fixed',
                        left: 0,
                        borderRight: '1px solid #f0f0f0',
                        padding: '16px',
                    }}
                >
                    {/* Заголовок с количеством комнат и кнопкой добавления */}
                    <div className="header_layouts">
                        <div className="left_HT_ptt">
                            <div className="Users">
                                <h1 className="name">Аудиокомнаты</h1>
                                <h1 className="count">({groups.length})</h1>
                            </div>
                            {user?.isAdmin && (
                                <ButtonAddPlus onClick={() => setIsCreateGroupModalVisible(true)} />
                            )}
                        </div>
                    </div>

                    {/* Поле поиска */}
                    <Input
                        placeholder="Поиск"
                        suffix={<SearchImg />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ marginBottom: '16px' }}
                    />

                    {/* Список комнат и устройств */}
                    <Menu
                        mode="inline"
                        theme="light"
                        style={{ borderRight: 0 }}
                        className="custom-menu-ptt body medium-bold"
                    >
                        <CustomSubMenu
                            key={'unsigned'}
                            unreadCounts={unreadCounts}
                            group={null}
                            devices={getAvailableDevices}
                            selectedGroup={selectedGroup}
                            selectedDevice={selectedDevice}
                            onSelect={selectTarget}
                            renderStatusDot={renderStatusDot}
                            renderMenuIcon={renderMenuIcon}
                        />

                        {filteredGroups.map((group) => (
                            <CustomSubMenu
                                unreadCounts={unreadCounts}
                                key={group.uid}
                                group={group}
                                selectedGroup={selectedGroup}
                                selectedDevice={selectedDevice}
                                onSelect={selectTarget}
                                renderStatusDot={renderStatusDot}
                                renderMenuIcon={renderMenuIcon}
                            />
                        ))}
                    </Menu>
                </Sider>

                {/* Основное содержимое (остается без изменений) */}
                <Layout style={{ marginLeft: 480 }}>
                    {currentTarget ? (
                        <>
                            <div
                                style={{
                                    position: 'fixed',
                                    bottom: 10,
                                    right: 10,
                                    padding: '5px 10px',
                                    background: isWsConnected ? '#52c41a' : '#f5222d',
                                    color: 'white',
                                    borderRadius: 4,
                                    zIndex: 1000,
                                }}
                            >
                                {isWsConnected ? 'Подключение Online' : 'Подключение Offline'}
                            </div>
                            <Header
                                style={{
                                    background: '#fff',
                                    padding: '0 24px',
                                    height: 'auto',
                                    lineHeight: 'normal',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '16px 0',
                                        borderBottom: '1px solid var(--divider-2)',
                                    }}
                                >
                                    <Title level={4} style={{ margin: 0 }}>
                                        {selectedDevice &&
                                            renderStatusDot(selectedDevice.online, true)}
                                        {currentTarget.name == 'unsigned'
                                            ? 'Все доступные устройства (несортированные)'
                                            : currentTarget.name}
                                    </Title>
                                    {currentTarget.type === 'group' && selectedGroup && (
                                        <div style={{ display: 'flex', marginLeft: 8 }}>
                                            <Button
                                                className={
                                                    'button-type-primary button-size-medium_icon'
                                                }
                                                icon={<EditImg />}
                                                style={{ marginRight: 8 }}
                                                onClick={() => {
                                                    const group = groups.find(
                                                        (g) => g.name === currentTarget?.name,
                                                    );
                                                    if (group) {
                                                        showEditModal(group);
                                                    }
                                                }}
                                            ></Button>
                                            <Button
                                                className={
                                                    'button-type-primary button-size-medium_icon button-state-danger'
                                                }
                                                icon={
                                                    <DeleteImg style={{ width: 16, height: 16 }} />
                                                }
                                                danger
                                                onClick={() => setDeleteGroupModal(true)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Header>

                            <Content
                                style={{
                                    padding: '20px',
                                    backgroundColor: 'var(--gray-white)',
                                    flex: 1,
                                    overflowY: 'auto',
                                    marginBottom: 80, // место для footer и кнопки
                                }}
                                className={'ptt-content'}
                                ref={chatContainerRef}
                            >
                                <MessageList
                                    key={currentTarget?.groupName}
                                    chatContainerRef={chatContainerRef}
                                    currentTarget={currentTarget}
                                    user={user}
                                />
                                <RecordingAnimation isRecording={isRecording} user={user!} />
                            </Content>

                            <Footer
                                style={{
                                    position: 'fixed',
                                    bottom: 0,
                                    width: 'calc(100% - 480px)',
                                    background: 'var(--bg-surface-2)',
                                    padding: '16px 24px',
                                    borderTop: '1px solid #f0f0f0',
                                    height: 104,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {currentTarget.id !== '00000000-0000-0000-0000-000000000000' && (
                                    <Button
                                        type="primary"
                                        ref={recordButtonRef}
                                        onClick={toggleRecording}
                                        shape="circle"
                                        className={'body medium-bold'}
                                        style={{
                                            width: 132,
                                            height: 132,
                                            bottom: 28,
                                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                                            backgroundColor: 'var(--primary)',
                                            color: '#fff',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        {isRecording
                                            ? `Отправить запись (${recordingTime}с)`
                                            : 'Нажмите, чтобы говорить'}
                                    </Button>
                                )}
                            </Footer>
                        </>
                    ) : (
                        <Content
                            style={{
                                padding: '20px',
                                backgroundColor: 'var(--gray-white)',
                                minHeight: 'calc(100vh - 64px)',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    flexDirection: 'column',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <img
                                        style={{ width: 108, height: 154 }}
                                        src={MicrophoneImg}
                                        alt="Микрофон"
                                    />
                                    <span className={'title large ptt-text'}>
                                        Выберите аудиокомнату или устройство для отправки
                                        аудиосообщения
                                    </span>
                                </div>
                            </div>
                        </Content>
                    )}
                </Layout>
            </Layout>

            <GroupModal
                form={formCreate}
                formName={'ptt-form-create'}
                title={
                    <div className="titleModal_Layout">
                        <span className="spanLayout">Добавить аудиогруппу</span>
                        <CloseOutlined
                            onClick={() => setIsCreateGroupModalVisible(false)}
                            className="closeBut_Layout"
                        />
                    </div>
                }
                textOk={'Добавить'}
                showCancel={false}
                visible={isCreateGroupModalVisible}
                onOk={(values) => handleCreateGroup(values)}
                onCancel={() => setIsCreateGroupModalVisible(false)}
                devices={allDevices}
                className={'ptt-modal-create'}
                allGroups={groups}
                users={users}
                currentUser={user}
            />

            <DeleteGroupModal
                title={'Удаление аудиокомнаты'}
                onCancel={() => setDeleteGroupModal(false)}
                setIsModalVisible={setDeleteGroupModal}
                onOk={() => handleDeleteGroup()}
                visible={deleteGroupModal}
                groupName={selectedGroup?.name ?? 'аудиокомната не определена'}
            />

            <GroupModal
                formName={'ptt-form-edit'}
                form={formEdit}
                visible={isEditModalVisible}
                onCancel={() => setIsEditModalVisible(false)}
                onOk={handleEditGroup}
                initialValues={editingGroup}
                title={
                    <div className="titleModal_Layout">
                        <span className="spanLayout">Редактировать аудиогруппу</span>
                        <CloseOutlined
                            onClick={() => setIsEditModalVisible(false)}
                            className="closeBut_Layout"
                        />
                    </div>
                }
                textOk={'Сохранить'}
                className={'ptt-modal-edit'}
                showCancel={false}
                devices={allDevices}
                allGroups={groups}
                users={users}
                currentUser={user}
                isEditMode={true}
            />
        </Layout>
    );
};

export default PTTDispatchV2;
